import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  AccountBalance as AssetsIcon,
  CreditCard as LiabilitiesIcon,
  TrendingUp as EquityIcon,
  Business as BusinessIcon
} from '@mui/icons-material';

import { exportToPDF, exportToExcel, exportToCSV } from '../../utils/exportUtils';

// Types
interface BalanceSheetData {
  as_of_date: string;
  assets: {
    current_assets: {
      cash_and_equivalents: number;
      accounts_receivable: number;
      inventory: number;
      prepaid_expenses: number;
      total_current_assets: number;
      cash_and_equivalents_formatted: string;
      accounts_receivable_formatted: string;
      inventory_formatted: string;
      prepaid_expenses_formatted: string;
      total_current_assets_formatted: string;
    };
    fixed_assets: {
      property_plant_equipment: number;
      accumulated_depreciation: number;
      intangible_assets: number;
      total_fixed_assets: number;
      property_plant_equipment_formatted: string;
      accumulated_depreciation_formatted: string;
      intangible_assets_formatted: string;
      total_fixed_assets_formatted: string;
    };
    total_assets: number;
    total_assets_formatted: string;
  };
  liabilities: {
    current_liabilities: {
      accounts_payable: number;
      accrued_expenses: number;
      short_term_debt: number;
      vat_liability: number;
      total_current_liabilities: number;
      accounts_payable_formatted: string;
      accrued_expenses_formatted: string;
      short_term_debt_formatted: string;
      vat_liability_formatted: string;
      total_current_liabilities_formatted: string;
    };
    long_term_liabilities: {
      long_term_debt: number;
      deferred_tax: number;
      total_long_term_liabilities: number;
      long_term_debt_formatted: string;
      deferred_tax_formatted: string;
      total_long_term_liabilities_formatted: string;
    };
    total_liabilities: number;
    total_liabilities_formatted: string;
  };
  equity: {
    share_capital: number;
    retained_earnings: number;
    current_year_profit: number;
    total_equity: number;
    share_capital_formatted: string;
    retained_earnings_formatted: string;
    current_year_profit_formatted: string;
    total_equity_formatted: string;
  };
  total_liabilities_equity: number;
  total_liabilities_equity_formatted: string;
}

interface BalanceSheetReportProps {
  startDate: Date;
  endDate: Date;
  loading: boolean;
  onExport: (format: string) => void;
}

const BalanceSheetReport: React.FC<BalanceSheetReportProps> = ({
  startDate,
  endDate,
  loading,
  onExport
}) => {
  const [reportData, setReportData] = useState<BalanceSheetData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const asOfDate = endDate; // Use endDate as the as-of date for balance sheet

  useEffect(() => {
    fetchBalanceSheetData();
  }, [endDate]); // Balance sheet uses end date as "as of" date

  const fetchBalanceSheetData = async () => {
    try {
      setError(null);
      const asOfDateStr = endDate.toISOString().split('T')[0];

      const response = await fetch(
        `/api/v1/reports/advanced/balance-sheet?as_of_date=${asOfDateStr}`
      );

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        throw new Error('Failed to fetch balance sheet data');
      }
    } catch (error) {
      console.error('Error fetching balance sheet data:', error);
      setError('Failed to load balance sheet report');
    }
  };

  const handleExport = (format: 'pdf' | 'excel' | 'csv') => {
    if (!reportData) {
      console.error('No report data available for export');
      return;
    }

    const reportInfo = {
      title: 'Balance Sheet Report',
      period: `As of ${asOfDate.toLocaleDateString('en-US')}`,
      data: reportData,
      type: 'balance-sheet' as const
    };

    try {
      switch (format) {
        case 'pdf':
          exportToPDF(reportInfo);
          break;
        case 'excel':
          exportToExcel(reportInfo);
          break;
        case 'csv':
          exportToCSV(reportInfo);
          break;
      }

      // Call the parent onExport for UI feedback
      onExport(format);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>Loading Balance Sheet...</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  if (!reportData) {
    return (
      <Alert severity="info">
        No data available for the selected date.
      </Alert>
    );
  }

  const formatTableRow = (label: string, amount: string, isTotal = false, isSubtotal = false, indent = 0) => (
    <TableRow>
      <TableCell sx={{
        fontWeight: isTotal ? 'bold' : isSubtotal ? 'medium' : 'normal',
        fontSize: isTotal ? '1.1rem' : 'inherit',
        pl: 2 + indent
      }}>
        {label}
      </TableCell>
      <TableCell align="right" sx={{
        fontWeight: isTotal ? 'bold' : isSubtotal ? 'medium' : 'normal',
        fontSize: isTotal ? '1.1rem' : 'inherit'
      }}>
        {amount}
      </TableCell>
    </TableRow>
  );

  return (
    <Box>
      {/* Report Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Balance Sheet
          </Typography>
          <Typography variant="body2" color="textSecondary">
            As of {new Date(reportData.as_of_date).toLocaleDateString('en-US')}
          </Typography>
        </Box>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <AssetsIcon color="primary" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Total Assets</Typography>
                  <Typography variant="h6">{reportData.assets.total_assets_formatted}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <LiabilitiesIcon color="warning" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Total Liabilities</Typography>
                  <Typography variant="h6">{reportData.liabilities.total_liabilities_formatted}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <EquityIcon color="success" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Total Equity</Typography>
                  <Typography variant="h6">{reportData.equity.total_equity_formatted}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <BusinessIcon color="info" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Net Worth</Typography>
                  <Typography variant="h6" color={reportData.equity.total_equity >= 0 ? 'success.main' : 'error.main'}>
                    {reportData.equity.total_equity_formatted}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Balance Sheet */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Account</strong></TableCell>
              <TableCell align="right"><strong>Amount</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Assets Section */}
            <TableRow>
              <TableCell colSpan={2} sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', fontWeight: 'bold' }}>
                ASSETS
              </TableCell>
            </TableRow>

            {/* Current Assets */}
            <TableRow>
              <TableCell sx={{ fontWeight: 'medium', bgcolor: 'grey.100' }}>Current Assets</TableCell>
              <TableCell></TableCell>
            </TableRow>
            {formatTableRow('Cash and Cash Equivalents', reportData.assets.current_assets.cash_and_equivalents_formatted, false, false, 1)}
            {formatTableRow('Accounts Receivable', reportData.assets.current_assets.accounts_receivable_formatted, false, false, 1)}
            {formatTableRow('Inventory', reportData.assets.current_assets.inventory_formatted, false, false, 1)}
            {formatTableRow('Prepaid Expenses', reportData.assets.current_assets.prepaid_expenses_formatted, false, false, 1)}
            <TableRow>
              <TableCell colSpan={2}><Divider /></TableCell>
            </TableRow>
            {formatTableRow('Total Current Assets', reportData.assets.current_assets.total_current_assets_formatted, false, true)}

            {/* Fixed Assets */}
            <TableRow>
              <TableCell sx={{ fontWeight: 'medium', bgcolor: 'grey.100', pt: 2 }}>Fixed Assets</TableCell>
              <TableCell></TableCell>
            </TableRow>
            {formatTableRow('Property, Plant & Equipment', reportData.assets.fixed_assets.property_plant_equipment_formatted, false, false, 1)}
            {formatTableRow('Less: Accumulated Depreciation', `(${reportData.assets.fixed_assets.accumulated_depreciation_formatted})`, false, false, 1)}
            {formatTableRow('Intangible Assets', reportData.assets.fixed_assets.intangible_assets_formatted, false, false, 1)}
            <TableRow>
              <TableCell colSpan={2}><Divider /></TableCell>
            </TableRow>
            {formatTableRow('Total Fixed Assets', reportData.assets.fixed_assets.total_fixed_assets_formatted, false, true)}

            <TableRow>
              <TableCell colSpan={2} sx={{ pt: 2 }}><Divider sx={{ borderWidth: 2 }} /></TableCell>
            </TableRow>
            {formatTableRow('TOTAL ASSETS', reportData.assets.total_assets_formatted, true)}

            {/* Liabilities Section */}
            <TableRow>
              <TableCell colSpan={2} sx={{ bgcolor: 'warning.light', color: 'warning.contrastText', fontWeight: 'bold', pt: 3 }}>
                LIABILITIES
              </TableCell>
            </TableRow>

            {/* Current Liabilities */}
            <TableRow>
              <TableCell sx={{ fontWeight: 'medium', bgcolor: 'grey.100' }}>Current Liabilities</TableCell>
              <TableCell></TableCell>
            </TableRow>
            {formatTableRow('Accounts Payable', reportData.liabilities.current_liabilities.accounts_payable_formatted, false, false, 1)}
            {formatTableRow('Accrued Expenses', reportData.liabilities.current_liabilities.accrued_expenses_formatted, false, false, 1)}
            {formatTableRow('Short-term Debt', reportData.liabilities.current_liabilities.short_term_debt_formatted, false, false, 1)}
            {formatTableRow('Sales Tax Liability', reportData.liabilities.current_liabilities.vat_liability_formatted, false, false, 1)}
            <TableRow>
              <TableCell colSpan={2}><Divider /></TableCell>
            </TableRow>
            {formatTableRow('Total Current Liabilities', reportData.liabilities.current_liabilities.total_current_liabilities_formatted, false, true)}

            {/* Long-term Liabilities */}
            <TableRow>
              <TableCell sx={{ fontWeight: 'medium', bgcolor: 'grey.100', pt: 2 }}>Long-term Liabilities</TableCell>
              <TableCell></TableCell>
            </TableRow>
            {formatTableRow('Long-term Debt', reportData.liabilities.long_term_liabilities.long_term_debt_formatted, false, false, 1)}
            {formatTableRow('Deferred Tax', reportData.liabilities.long_term_liabilities.deferred_tax_formatted, false, false, 1)}
            <TableRow>
              <TableCell colSpan={2}><Divider /></TableCell>
            </TableRow>
            {formatTableRow('Total Long-term Liabilities', reportData.liabilities.long_term_liabilities.total_long_term_liabilities_formatted, false, true)}

            <TableRow>
              <TableCell colSpan={2} sx={{ pt: 2 }}><Divider sx={{ borderWidth: 2 }} /></TableCell>
            </TableRow>
            {formatTableRow('TOTAL LIABILITIES', reportData.liabilities.total_liabilities_formatted, true)}

            {/* Equity Section */}
            <TableRow>
              <TableCell colSpan={2} sx={{ bgcolor: 'success.light', color: 'success.contrastText', fontWeight: 'bold', pt: 3 }}>
                EQUITY
              </TableCell>
            </TableRow>
            {formatTableRow('Share Capital', reportData.equity.share_capital_formatted)}
            {formatTableRow('Retained Earnings', reportData.equity.retained_earnings_formatted)}
            {formatTableRow('Current Year Profit/Loss', reportData.equity.current_year_profit_formatted)}
            <TableRow>
              <TableCell colSpan={2} sx={{ pt: 2 }}><Divider sx={{ borderWidth: 2 }} /></TableCell>
            </TableRow>
            {formatTableRow('TOTAL EQUITY', reportData.equity.total_equity_formatted, true)}

            {/* Total Check */}
            <TableRow>
              <TableCell colSpan={2} sx={{ pt: 2 }}><Divider sx={{ borderWidth: 3, borderColor: 'primary.main' }} /></TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: 'primary.light' }}>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                TOTAL LIABILITIES & EQUITY
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                {reportData.total_liabilities_equity_formatted}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Balance Check */}
      <Alert
        severity={reportData.assets.total_assets === reportData.total_liabilities_equity ? 'success' : 'error'}
        sx={{ mt: 2 }}
      >
        <Typography variant="body2">
          <strong>Balance Check:</strong> {reportData.assets.total_assets === reportData.total_liabilities_equity
            ? 'Assets equal Liabilities + Equity ✓'
            : 'Balance sheet does not balance ✗'}
        </Typography>
      </Alert>
    </Box>
  );
};

export default BalanceSheetReport;
